import React, { Fragment, useContext } from 'react'
import { Link } from 'react-router-dom'

import { ROUTE_TYPE } from '../../Configuration'
import { DueOut } from '../../assets/ts/data/dueout/dueout'
import { convertDate, localDateToUTCDate } from '../../assets/ts/date-and-time/date-and-time'
import { defaultToEmptyString } from '../../assets/ts/formatting/formatting'
import { handleScroll, toShortDateString } from '../../assets/ts/general/general'
import { SelectionContext } from '../../store/selection-context-provider'
import { DUEOUT_STATUS } from '../../utils/constants/global-constants'
import { PersonDetails } from '../../utils/constants/global-interfaces'
import NO_RESULTS from '../../utils/constants/no-results'
import InputCheckbox from '../../utils/controls/input-checkbox'
import './table.css'

type Props = {
  count: string
  dueOuts: DueOut[]
  onBottomScrollReached?: () => void
  onTopScrollReached?: () => void
  title: string
}

function Row(dueouts: DueOut[], selection) {
  const row = dueouts?.map((dueout: DueOut) => {
    const guid = dueout.DueOutGUID ?? ''

    return (
      <Fragment key={guid}>
        <tr>
          <th className='checkbox'>
            <InputCheckbox
              formClassName='form-horizontal font-0'
              inputProps={{
                checked: selection.keyIsSelected(dueout.DueOutGUID),
                id: dueout.DueOutGUID,
                label: `checkbox-${dueout.DueOutID}`,
                name: 'Input Check',
                type: 'checkbox',
              }}
              onChange={(e) => selection.handleSingleCheckboxChange(e, dueout.DueOutGUID)}
            />
          </th>
          <th aria-label={dueout.DueOutID} tabIndex={0}>
            <Link
              className='links'
              state={{ prevPath: '/completedinquiries' }}
              title={dueout.DueOutID}
              to={`${ROUTE_TYPE}dueoutdetailview/${dueout.DueOutGUID}`}
            >
              {defaultToEmptyString(dueout.DueOutID)}
            </Link>
          </th>
          <td aria-label={dueout['Section/MetadataValue']} tabIndex={0}>
            {defaultToEmptyString(dueout['Section/MetadataValue'])}
          </td>
          <td aria-label={dueout['Category/MetadataValue']} tabIndex={0}>
            {defaultToEmptyString(dueout['Category/MetadataValue'])}
          </td>
          <td aria-label={dueout['SubCategory/MetadataValue']} tabIndex={0}>
            {defaultToEmptyString(dueout['SubCategory/MetadataValue'])}
          </td>
          <td aria-label={dueout.ServicingBy} tabIndex={0}>
            {defaultToEmptyString(dueout.ServicingBy)}
          </td>
          <td aria-label={dueout.Priority} tabIndex={0}>
            {defaultToEmptyString(dueout.Priority)}
          </td>
          <td aria-label={toShortDateString(dueout.ItemCreated)} tabIndex={0}>
            {defaultToEmptyString(toShortDateString(dueout.ItemCreated))}
          </td>
          <td
            aria-label={defaultToEmptyString(toShortDateString(
              dueout.DueDate === null 
              ? undefined : convertDate(localDateToUTCDate(dueout.DueDate), '') 
            ))}
            tabIndex={0}
          >
            {defaultToEmptyString(toShortDateString(
            dueout.DueDate === null 
            ? undefined : convertDate(localDateToUTCDate(dueout.DueDate), '') 
          ))}
          </td>
          <td aria-label={dueout.ItemModifiedBy} tabIndex={0}>
            {defaultToEmptyString(dueout.ItemModifiedBy.properties.defaultDisplay)}
          </td>
          <th
            aria-label={dueout['Status/StatusDescription']}
            data-table-status={DUEOUT_STATUS[dueout.StatusCode ?? '']?.colorCode}
            tabIndex={0}
          >
            {defaultToEmptyString(dueout['Status'])}
          </th>
        </tr>

        <tr>
          <th colSpan={12}>
            <div className='d-flex align-items-center'>
              <Link
                className='links'
                state={{ prevPath: '/completedinquiries' }}
                title={dueout.Subject}
                to={`${ROUTE_TYPE}dueoutdetailview/${dueout.DueOutGUID}`}
              >
                {defaultToEmptyString(dueout.Subject)}
              </Link>
              <div className='dividerdashed vertical mx-2'></div>
              <div
                aria-label={(dueout.Customer as PersonDetails)?.properties?.defaultDisplay}
                className='m-0 p-0 userinfo title-color15 font-11 text-uppercase poppins-semibold'
                tabIndex={0}
              >
                <span className='icon-user '></span>
                {defaultToEmptyString(
                  (dueout.Customer as PersonDetails)?.properties?.defaultDisplay
                )}
              </div>
            </div>
          </th>
        </tr>
      </Fragment>
    )
  })
  if (dueouts.length > 0) {
    return row
  } else {
    return (
      <tr className='no-results'>
        <td colSpan='25'>
          <NO_RESULTS></NO_RESULTS>
        </td>
      </tr>
    )
  }
}
const CompletedDueoutsTable = ({ dueOuts, onBottomScrollReached, onTopScrollReached }: Props) => {
  const selection = useContext(SelectionContext)

  return (
    <div
      className='w-100 overflow-auto px-1 inquiriestable'
      id='scrolltable'
      onScroll={(e) => handleScroll(e, onTopScrollReached, onBottomScrollReached)}
    >
      <table className='table-borderless table-dis w-100 sticky-top second-column last-column table-rowgap'>
        <thead>
          <tr>
            <th>
              <InputCheckbox
                formClassName='form-horizontal font-0'
                inputProps={{
                  checked: selection.allCheckboxIsChecked,
                  id: 'all',
                  label: 'All',
                  name: 'Input Check',
                  type: 'checkbox',
                }}
                onChange={selection.handleAllCheckboxChange}
              />
            </th>
            <th aria-label='ID' tabIndex={0}>
              ID
            </th>
            <th aria-label='Section' tabIndex={0}>
              Section
            </th>
            <th aria-label='Category' tabIndex={0}>
              Category
            </th>
            <th aria-label='Sub Category' tabIndex={0}>
              Sub Category
            </th>
            <th aria-label='Current Servicing Unit' tabIndex={0}>
              Current Servicing Unit
            </th>
            <th aria-label='Priority' tabIndex={0}>
              Priority
            </th>
            <th aria-label='Submitted Date' tabIndex={0}>
              Submitted Date
            </th>
            <th aria-label='Due Date' tabIndex={0}>
              Due Date
            </th>
            <th aria-label='Status Modified By' tabIndex={0}>
              Status Modified By
            </th>
            <th aria-label='Status' tabIndex={0}>
              Status
            </th>
          </tr>
        </thead>
        {dueOuts?.length > 0 ? (
          <tbody>{Row(dueOuts, selection)}</tbody>
        ) : (
          <tr className='no-results'>
            <td colSpan='25'>
              <NO_RESULTS></NO_RESULTS>
            </td>
          </tr>
        )}
      </table>
    </div>
  )
}
export default CompletedDueoutsTable
